import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, assetUrl } from '../api/index.js';
import LanguageSelector from '../components/LanguageSelector.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useLocale } from '../contexts/LocaleContext.jsx';

export default function GolferProfile() {
  const { id } = useParams();
  const { user, refreshUser } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();
  const avatarInputRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [editForm, setEditForm] = useState({ status_line: '', bio: '', languages: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const isOwn = user?.sub === id || user?.id === id;

  useEffect(() => {
    setLoading(true);
    (isOwn ? api.users.me() : api.users.getById(id))
      .then((data) => {
        if (data.role !== 'golfer') {
          navigate(`/caddies/${id}`, { replace: true });
          return;
        }
        setProfile(data);
        setEditForm({
          status_line: data.profile?.status_line ?? '',
          bio: data.profile?.bio ?? '',
          languages: data.profile?.languages ?? [],
        });
      })
      .catch(() => navigate('/feed'))
      .finally(() => setLoading(false));
  }, [id, isOwn, navigate]);

  async function handleAvatarChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const { avatar_url } = await api.users.uploadAvatar(formData);
      setProfile((current) => ({ ...current, avatar_url }));
      await refreshUser();
    } catch (err) {
      alert(err.message);
    } finally {
      setAvatarUploading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await api.golfers.updateProfile(editForm);
      setProfile((current) => ({ ...current, profile: editForm }));
      setEditMode(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <div className="relative shrink-0">
            {profile.avatar_url ? (
              <img src={assetUrl(profile.avatar_url)} alt={profile.display_name} className="w-24 h-24 rounded-full object-cover" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 text-3xl font-bold">
                {profile.display_name?.[0]?.toUpperCase()}
              </div>
            )}
            {isOwn && (
              <>
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={avatarUploading}
                  className="absolute bottom-0 right-0 bg-brand-700 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm shadow hover:bg-brand-800"
                  title={t('changePhoto')}
                >
                  {avatarUploading ? '...' : '📷'}
                </button>
                <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
              </>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h1 className="text-xl font-bold text-gray-900">{profile.display_name}</h1>
                <span className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full bg-brand-100 text-brand-700">
                  🏌️ {t('golfer')}
                </span>
              </div>
              {isOwn ? (
                <button onClick={() => setEditMode((current) => !current)} className="btn-secondary text-sm">
                  {editMode ? t('cancel') : t('editProfile')}
                </button>
              ) : (
                <button onClick={() => navigate(`/messages/${id}`)} className="btn-primary text-sm">
                  💬 {t('message')}
                </button>
              )}
            </div>

            {isOwn && editMode ? (
              <div className="mt-4 space-y-3">
                <input
                  className="input text-sm"
                  placeholder={t('statusLine')}
                  value={editForm.status_line}
                  onChange={(event) => setEditForm((current) => ({ ...current, status_line: event.target.value }))}
                  maxLength={120}
                />
                <div>
                  <p className="mb-1.5 text-sm font-medium text-gray-700">{t('languagesSpoken')}</p>
                  <LanguageSelector
                    value={editForm.languages}
                    onChange={(languages) => setEditForm((current) => ({ ...current, languages }))}
                  />
                </div>
                <textarea
                  className="input text-sm resize-none min-h-[100px]"
                  placeholder={t('bio')}
                  value={editForm.bio}
                  onChange={(event) => setEditForm((current) => ({ ...current, bio: event.target.value }))}
                  maxLength={500}
                />
                <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
                  {saving ? t('saving') : t('saveChanges')}
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-3 text-sm text-gray-600">
                {profile.profile?.status_line && <p className="text-base font-medium text-gray-800">{profile.profile.status_line}</p>}
                {profile.profile?.languages?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {profile.profile.languages.map((language) => (
                      <span key={language} className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                        {language}
                      </span>
                    ))}
                  </div>
                )}
                {profile.profile?.bio && <p className="whitespace-pre-wrap text-gray-700">{profile.profile.bio}</p>}
                {!profile.profile?.status_line && !profile.profile?.bio && !profile.profile?.languages?.length && (
                  <p className="text-gray-400">{t('emptyGolferProfile')}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
